/*
 * ChecklistCard.jsx
 * -----------------
 * Card component shown on the Checklists page representing
 * a single checklist. Displays title, category, progress,
 * streak, item count, nearest due date, and recurring status.
 * Clicking navigates to ChecklistDetail.
 * Supports drag and drop reordering via dnd-kit.
 * Used exclusively on the Checklists page.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import CategoryTag from "./CategoryTag";
import ProgressBar from "./ProgressBar";
import StreakBadge from "./StreakBadge";
import CountdownBadge from "./CountdownBadge";

// ============================================
// RECURRING INDICATOR
// ============================================
function RecurringIndicator({ interval }) {
  if (!interval) return null;

  const labels = {
    daily: "Daily",
    weekly: "Weekly",
    monthly: "Monthly",
    sunday: "Every Sunday",
    monday: "Every Monday",
    tuesday: "Every Tuesday",
    wednesday: "Every Wednesday",
    thursday: "Every Thursday",
    friday: "Every Friday",
    saturday: "Every Saturday",
  };

  return (
    <span style={{
      fontFamily: "var(--font-body)",
      fontSize: "11px",
      fontWeight: 500,
      color: "var(--text-muted)",
      display: "flex",
      alignItems: "center",
      gap: "3px"
    }}>
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path
          d="M1 5C1 2.8 2.8 1 5 1C6.5 1 7.8 1.8 8.5 3M9 5C9 7.2 7.2 9 5 9C3.5 9 2.2 8.2 1.5 7"
          stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"
        />
        <path d="M8 1L8.5 3L6.5 3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2 9L1.5 7L3.5 6.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      {labels[interval] || interval}
    </span>
  );
}

// ============================================
// NEAREST DUE DATE
// Finds the soonest upcoming due date across all items
// ============================================
function getNearestDueDate(items) {
  const now = new Date();
  const dueDates = items
    .filter(item => !item.checked && item.dueDate)
    .map(item => new Date(item.dueDate))
    .sort((a, b) => a - b);

  if (dueDates.length === 0) return null;

  const nearest = dueDates[0];
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);

  const isOverdue = nearest < todayStart;
  const isToday = nearest >= todayStart && nearest <= todayEnd;

  return { date: nearest, isOverdue, isToday };
}

// ============================================
// DUE DATE CHIP
// ============================================
function DueDateChip({ items }) {
  const nearest = getNearestDueDate(items);
  if (!nearest) return null;

  const { date, isOverdue, isToday } = nearest;

  const label = isToday
    ? "Due today"
    : isOverdue
      ? `Overdue · ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
      : date.toLocaleDateString("en-US", { month: "short", day: "numeric" });

  const color = isOverdue
    ? "var(--status-overdue)"
    : isToday
      ? "var(--status-today)"
      : "var(--text-muted)";

  const bg = isOverdue
    ? "var(--status-overdue-muted)"
    : isToday
      ? "var(--accent-green-muted)"
      : "var(--bg-surface-2)";

  return (
    <span style={{
      fontFamily: "var(--font-body)",
      fontSize: "11px",
      fontWeight: isOverdue || isToday ? 600 : 400,
      color,
      background: bg,
      borderRadius: "100px",
      padding: "2px 8px",
      display: "inline-flex",
      alignItems: "center",
      gap: "4px"
    }}>
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <rect x="1" y="2" width="8" height="7" rx="1" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M3 1V3M7 1V3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
        <path d="M1 5H9" stroke="currentColor" strokeWidth="1.2"/>
      </svg>
      {label}
    </span>
  );
}

// ============================================
// MAIN EXPORT
// ============================================
export default function ChecklistCard({
  checklist,
  dragHandleListeners,
  dragHandleAttributes,
  isDragging = false,
  theme
}) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);

  const { id, title, category, items = [], streak, recurring, recurringInterval, targetDate } = checklist;

  // Compute stats
  const total = items.length;
  const completed = items.filter(i => i.checked).length;
  const hasOverdue = items.some(i => {
    if (i.checked || !i.dueDate) return false;
    return new Date(i.dueDate) < new Date();
  });

  return (
    <div
      onClick={() => navigate(`/checklists/${id}`)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "var(--bg-surface)",
        border: `1px solid ${hasOverdue ? "var(--status-overdue)" : hovered ? "var(--accent-green)" : "var(--border)"}`,
        borderRadius: "var(--radius-md)",
        padding: "var(--space-5)",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-3)",
        boxShadow: isDragging
          ? "var(--shadow-lg)"
          : hovered
            ? "var(--shadow-md)"
            : "var(--shadow-sm)",
        transform: isDragging
          ? "rotate(2deg) scale(1.02)"
          : hovered
            ? "translateY(-2px)"
            : "translateY(0)",
        transition: "border-color var(--transition-fast), box-shadow var(--transition-fast), transform var(--transition-fast)",
        opacity: isDragging ? 0.9 : 1,
        position: "relative",
        overflow: "hidden"
      }}
    >

      {/* Overdue accent bar */}
      {hasOverdue && (
        <div style={{
          position: "absolute",
          top: 0, left: 0, right: 0,
          height: "3px",
          background: "var(--status-overdue)"
        }} />
      )}

      {/* Top row -- category + streak + drag handle */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "var(--space-2)"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
          <CategoryTag category={category} size="sm" theme={theme} />
          {recurring && <RecurringIndicator interval={recurringInterval} />}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
          <StreakBadge streak={streak} size="sm" />

          {/* Drag handle */}
          <div
            {...dragHandleListeners}
            {...dragHandleAttributes}
            onClick={e => e.stopPropagation()}
            style={{
              opacity: hovered ? 1 : 0,
              transition: "opacity var(--transition-fast)",
              cursor: "grab",
              color: "var(--text-muted)",
              display: "flex",
              flexDirection: "column",
              gap: "3px",
              padding: "var(--space-1)",
              touchAction: "none"
            }}
          >
            {[0, 1].map(i => (
              <div key={i} style={{ display: "flex", gap: "3px" }}>
                <div style={{ width: "3px", height: "3px", borderRadius: "50%", background: "currentColor" }} />
                <div style={{ width: "3px", height: "3px", borderRadius: "50%", background: "currentColor" }} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Title */}
      <h3 style={{
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: "18px",
        color: "var(--text-primary)",
        margin: 0,
        lineHeight: 1.3,
        wordBreak: "break-word"
      }}>
        {title}
      </h3>

      {/* Progress bar */}
      {total > 0 && (
        <ProgressBar
          total={total}
          completed={completed}
          height={5}
          showLabel={false}
          showCount={false}
        />
      )}

      {/* Bottom row -- item count, due date, countdown */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "var(--space-2)"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap" }}>

          {/* Item count */}
          <span style={{
            fontFamily: "var(--font-body)",
            fontSize: "12px",
            color: "var(--text-muted)"
          }}>
            {total === 0
              ? "No items"
              : `${completed} of ${total} done`}
          </span>

          <DueDateChip items={items} />
        </div>

        {/* Countdown badge */}
        {targetDate && (
          <CountdownBadge targetDate={targetDate} />
        )}
      </div>
    </div>
  );
}